import type { Event, EventLifecycleStage, EventStatus } from "@/types/domain/event";
import type { WeddingDetails } from "@/types/domain/wedding-details";
import { WEDDING_EVENT_TYPE } from "@/types/domain/event";
import { DUMMY_USER } from "@/lib/dummy-data";

/**
 * Dummy Event + WeddingDetails fixtures, seeded with the SAME ids the
 * legacy `DUMMY_WEDDINGS` fixtures used (wed_...0001 / ...0002 / ...0003).
 *
 * This is what makes the Wedding compatibility layer safe: Guests,
 * Budget, and Checklist dummy data are keyed by `weddingId`, and
 * `activeWedding.id` (derived from the active Event via
 * `eventToWedding`) resolves to these same ids — so nothing downstream
 * had to change ids or re-seed its fixtures.
 */

function splitCoupleNames(coupleNames: string): { partnerOneName: string; partnerTwoName: string } {
  const [first, second] = coupleNames.split("&").map((part) => part.trim());
  return { partnerOneName: first ?? coupleNames, partnerTwoName: second ?? "" };
}

/** Wedding lifecycle stage -> generic Event lifecycle stage, used when creating/updating events from the Wedding-shaped form. */
export function weddingStageToLifecycleStage(stage: string): EventLifecycleStage {
  switch (stage) {
    case "inquiry":
      return "inquiry";
    case "booked":
      return "booked";
    case "planning":
      return "planning";
    case "final_month":
      return "final_review";
    case "completed":
      return "completed";
    case "archived":
      return "archived";
    default:
      return "inquiry";
  }
}

/** Generic Event lifecycle stage -> Wedding stage, used when reading events back through the compatibility view. */
export function lifecycleStageToWeddingStage(stage: EventLifecycleStage): string {
  switch (stage) {
    case "inquiry":
    case "proposal":
      return "inquiry";
    case "booked":
      return "booked";
    case "planning":
      return "planning";
    case "production":
    case "final_review":
    case "event_day":
      return "final_month";
    case "completed":
      return "completed";
    case "archived":
      return "archived";
    default:
      return "inquiry";
  }
}

function eventStatusForStage(stage: EventLifecycleStage): EventStatus {
  if (stage === "completed") return "completed";
  if (stage === "archived") return "archived";
  return "active";
}

interface WeddingSeed {
  id: string;
  organizationId: string;
  coupleNames: string;
  weddingDate: string;
  venueName?: string;
  venueCity?: string;
  stage: string;
  guestCountEstimate?: number;
  budgetTotal?: number;
  createdAt: string;
}

const WEDDING_SEEDS: WeddingSeed[] = [
  {
    id: "wed_01HZX0000000000000000001",
    organizationId: "org_01HZX0000000000000000001",
    coupleNames: "Sofía & Marco",
    weddingDate: "2026-10-17",
    venueName: "Hacienda Los Laureles",
    venueCity: "Guadalajara, Jalisco",
    stage: "planning",
    guestCountEstimate: 140,
    budgetTotal: 850000,
    createdAt: "2026-02-01T09:00:00.000Z",
  },
  {
    id: "wed_01HZX0000000000000000002",
    organizationId: "org_01HZX0000000000000000001",
    coupleNames: "Elena & Daniel",
    weddingDate: "2026-08-22",
    venueName: "Casa Luna Jardín",
    venueCity: "Tlaquepaque, Jalisco",
    stage: "final_month",
    guestCountEstimate: 90,
    budgetTotal: 520000,
    createdAt: "2025-11-20T09:00:00.000Z",
  },
  {
    id: "wed_01HZX0000000000000000003",
    organizationId: "org_01HZX0000000000000000001",
    coupleNames: "Valentina & Diego",
    weddingDate: "2027-02-14",
    stage: "inquiry",
    guestCountEstimate: 60,
    createdAt: "2026-06-10T09:00:00.000Z",
  },
];

export const DUMMY_EVENTS: Event[] = WEDDING_SEEDS.map((seed) => {
  const lifecycleStage = weddingStageToLifecycleStage(seed.stage);
  return {
    id: seed.id,
    organizationId: seed.organizationId,
    eventTypeId: WEDDING_EVENT_TYPE.id,
    title: seed.coupleNames,
    status: eventStatusForStage(lifecycleStage),
    lifecycleStage,
    startDate: seed.weddingDate,
    timezone: "America/Mexico_City",
    venueName: seed.venueName,
    venueCity: seed.venueCity,
    guestCountEstimate: seed.guestCountEstimate,
    budgetEstimate: seed.budgetTotal,
    currency: "MXN",
    createdBy: DUMMY_USER.id,
    createdAt: seed.createdAt,
    updatedAt: seed.createdAt,
  };
});

export const DUMMY_WEDDING_DETAILS: WeddingDetails[] = WEDDING_SEEDS.map((seed) => {
  const { partnerOneName, partnerTwoName } = splitCoupleNames(seed.coupleNames);
  return {
    eventId: seed.id,
    partnerOneName,
    partnerTwoName,
    createdAt: seed.createdAt,
    updatedAt: seed.createdAt,
  };
});
